import Member from '@@types/Member';
import { Organization } from '@@types/Organization';

import { MemberRole, TenantMemberRole } from '~/constants/member';

export function isTenantOwnerOrAdmin(tenantMemberRole: TenantMemberRole) {
  return tenantMemberRole === MemberRole.OWNER || tenantMemberRole === MemberRole.ADMIN;
}

export function isAdminOrganization(organizationId: string, memberMe: Member) {
  return memberMe.organizationMemberRoleMap[organizationId]?.role === MemberRole.ADMIN;
}

export function isTenantOrOrganizationAdmin(memberMe: Member) {
  return (
    isTenantOwnerOrAdmin(memberMe.tenantMemberRole) || isAdminOrganization(memberMe.defaultOrganizationId, memberMe)
  );
}

function isArchiveAdminOrganization(organizationId: string, memberMe: Member) {
  return (
    memberMe?.tenantMemberGroupMap?.archiveAdmin?.tenantMemberGroupOption?.manageableOrganizationIds ?? []
  ).includes(organizationId);
}

function isAuditAdminOrganization(organizationId: string, memberMe: Member) {
  const tenantMemberGroupOption = memberMe?.tenantMemberGroupMap?.auditAdmin?.tenantMemberGroupOption;
  const manageAllOrganizations = tenantMemberGroupOption?.manageAllOrganizations ?? false;
  const manageableOrganizationIds = tenantMemberGroupOption?.manageableOrganizationIds ?? [];
  return manageAllOrganizations || manageableOrganizationIds.includes(organizationId);
}

// 실제 조직 Admin 인 경우
export function getAdminOrgs({ memberMe, organizations }: { memberMe: Member; organizations: Organization[] }) {
  return organizations.filter((org) => isAdminOrganization(org.id, memberMe));
}

export function canAccessAdminOrganization({
  memberMe,
  organizationId,
}: {
  memberMe: Member;
  organizationId: string;
}): boolean {
  return (
    isTenantOwnerOrAdmin(memberMe.tenantMemberRole) ||
    isAdminOrganization(organizationId, memberMe) ||
    isAuditAdminOrganization(organizationId, memberMe) ||
    isArchiveAdminOrganization(organizationId, memberMe)
  );
}

export function makeCanAccessAdminOrganizations({
  memberMe,
  organizations,
}: {
  memberMe: Member;
  organizations: Organization[];
}) {
  if (isTenantOwnerOrAdmin(memberMe.tenantMemberRole)) {
    return organizations;
  }
  return organizations.filter(
    (organization) =>
      isAdminOrganization(organization.id, memberMe) ||
      isAuditAdminOrganization(organization.id, memberMe) ||
      isArchiveAdminOrganization(organization.id, memberMe)
  );
}

/**
 * - 어드민 권한을 하나라도 가진 경우 `true`
 * - 확인하는 권한:
 *   - Tenant의 OWNER, ADMIN
 *   - 속한 Organization 중 하나 이상의 ADMIN
 *   - archiveAdmin
 *   - auditAdmin
 *   - resourceReservationAdmin
 *   - workflowAdmin
 *   - workScheduleAdmin
 */
export function isAdminOfAnyType(memberMe: Member) {
  if (isTenantOwnerOrAdmin(memberMe.tenantMemberRole)) {
    return true;
  }

  if (Object.values(memberMe.organizationMemberRoleMap).some(({ role }) => role === MemberRole.ADMIN)) {
    return true;
  }
  if (memberMe.tenantMemberGroupMap) {
    return Boolean(
      memberMe.tenantMemberGroupMap.archiveAdmin ||
        memberMe.tenantMemberGroupMap.auditAdmin ||
        memberMe.tenantMemberGroupMap.resourceReservationAdmin ||
        memberMe.tenantMemberGroupMap.workflowAdmin ||
        memberMe.tenantMemberGroupMap.workScheduleAdmin
    );
  }

  return false;
}
